import {
    FloaterLocation,
    FloaterModal,
    FloaterState,
    FloatingRoomFloaterState,
    FloatingRoomModal,
    MODALSTATE,
    MODAL_STACK,
    ModalKind,
    PREVIEW_SIZES,
    RoomView,
    closeFloatingPreview,
    generateModalKey,
    getFloaterOrientation,
    getNewExpandLocation,
    setModal,
    setModalStack,
    setMultiModal,
    setShowMultiModal,
} from '.'
import {
    clearPMUnreadCount,
    openMessage,
    resumePMUnreadCount,
} from '../../actions/message'
import { closeRoom } from '../../actions/room'
import { setLargePreviewPinnedIDState } from '../../app-state/state.grid'
import { getScreenState, type Size } from '../../app-state/state.screen'
import { COMPONENT_HEIGHTS, COMPONENT_WIDTHS } from '../../components/theme'
import {
    FLOATING_PREVIEW,
    FLOATING_ROOM,
    FloaterSize,
    MY_WEBCAM,
    QUICK_REPLY,
} from '../../constants/floaters'
import { Metadata, setRecipient } from '../../mfcLogic/pm'
import { isTouchDevice } from '../../utilities/browser'
import { APPSTATE } from '../state.app'
import { LAYOUTSTATE } from '../state.layout'
import { PMSTATE } from '../state.pm'
import { getSlugAndId, getUser } from '../state.users'
import { VIDEOSTATE } from '../state.video'

export const closeFloater = (id: number, type: string) => {
    // console.log('[closeFloater]', id, type)
    if (type === QUICK_REPLY) {
        resumePMUnreadCount(id)
        updateLocalStorage(id, { open: false })
    }
    if (type === FLOATING_PREVIEW) {
        setLargePreviewPinnedIDState(null)
        closeFloatingPreview(id)
        return
    }
    if (type === FLOATING_ROOM) {
        closeFloatingRoom(id)
        return
    }
    setShowMultiModal(generateModalKey(type, id), false, true)
}

const DEFAULT_LOCATION = {
    x: 40,
    y: 40,
    speedX: 0,
    speedY: 0,
    width: 0,
}

const DEFAULT_PREVIEW_STATE = {
    // id,
    minimized: false,
    // active: true,
    locations: {
        portrait: {
            x: 8,
            y: 8,
            speedX: 0,
            speedY: 0,
            width: 0,
        },
        landscape: {
            x: 8,
            y: 8,
            speedX: 0,
            speedY: 0,
            width: 0,
        },
    },
    size: FloaterSize.Small,
    sticky: false, // room preview or not (will stay open on nav)
    stickyHeader: false, // shrink/grow controls
    close: false, // close button
}

export const getTopFloaterLocation = () => {
    // check the stack for the first floater modal key and return its x,y position, otherwise return 0,0
    const foundModalKey = MODAL_STACK.byPosition.find((key) => {
        // 1) check what we're dealing with
        // - if it's a multimodal (floater) then we found something
        if (key.includes('-')) return true
        // - if its a floating room then we can use that too
        if (key === FLOATING_ROOM) return true
        // - MY_WEBCAM is probably not usable as it's likely to be placed at the edge already
        // - otherwise go to the next one
        return false
    })

    if (foundModalKey) {
        const { state } =
            foundModalKey === FLOATING_ROOM
                ? MODALSTATE.floating_room
                : MODALSTATE.floater[foundModalKey]
        if (state && state.locations) {
            const [orientation] = getFloaterOrientation()
            const location = state.locations[orientation] || {}
            return { x: location.x ?? 0, y: location.y ?? 0 }
        }
    }
    return { x: 0, y: 0 }
}

export const closeTopFloater = () => {
    const foundModalKey = MODAL_STACK.byPosition.find((key) => {
        // 1) check what we're dealing with
        // - if it's a multimodal (floater) then we found something
        if (key.includes('-') && MODALSTATE.floater[key].show) return true
        // - if its a floating room then we can use that too
        if (
            key === FLOATING_ROOM &&
            MODALSTATE.floating_room.show &&
            MODALSTATE.floating_room.state.size < FloaterSize.Fullscreen
        )
            return true

        // - MY_WEBCAM is probably not usable as it's likely to be placed at the edge already
        // - otherwise go to the next one
        return false
    })
    if (foundModalKey && !LAYOUTSTATE.layout.fullWindow) {
        const floater =
            foundModalKey === FLOATING_ROOM
                ? MODALSTATE.floating_room
                : MODALSTATE.floater[foundModalKey]
        closeFloater(floater.state.id, floater.type)
    }
}

export const calcPreviewLocation = (
    size: FloaterSize,
    windowSize?: Size,
): FloaterLocation => {
    const [, portrait] = getFloaterOrientation()
    const offsetX = !portrait
        ? LAYOUTSTATE.layout.expandedNav
            ? COMPONENT_WIDTHS.leftNavExpanded
            : COMPONENT_WIDTHS.leftNav
        : 8
    const { windowHeight, windowWidth } = getScreenState()
    const foundSize = windowSize ?? { width: windowWidth, height: windowHeight }
    const calcWidth = PREVIEW_SIZES[size]()
    const videoHeight = (calcWidth / 16) * 9
    const previewHeight = videoHeight + (size > FloaterSize.Medium ? 64 : 0)
    const offsetY = `${
        Math.min((foundSize.height - previewHeight) / foundSize.height, 0.7) *
        100
    }%`
    // get around the fact that `FloaterLocation` should be a number,
    // but we use a percentage for initial Y positioning
    return {
        x: offsetX,
        y: offsetY as any,
        width: calcWidth,
        height:
            size > FloaterSize.Small
                ? previewHeight + COMPONENT_HEIGHTS.chatHeader
                : previewHeight,
    }
}

/** Close all floating previews */
export const clearAllPreviews = () => {
    Object.values(MODALSTATE.floater).forEach((modal: FloaterModal) => {
        if (modal.show && modal.type === FLOATING_PREVIEW && !modal.state.close)
            setMultiModal(
                generateModalKey(FLOATING_PREVIEW, modal.state.id),
                FLOATING_PREVIEW,
                modal.state,
                false,
                true,
            )
    })
}

export const setFloatingPreview = (
    idOrSlug: number | string,
    show: boolean,
    state?: FloaterState,
    location?: FloaterLocation,
) => {
    const [, id] = getSlugAndId(idOrSlug)
    const modalKey = generateModalKey(FLOATING_PREVIEW, id)
    const existingPreview = MODALSTATE.floater[modalKey]

    const [orientation] = getFloaterOrientation()

    const newLocation = location && {
        [orientation]: location,
    }
    const newPreview = {
        id,
        ...DEFAULT_PREVIEW_STATE,
        ...existingPreview?.state,
        ...state,
        locations: {
            ...DEFAULT_PREVIEW_STATE.locations,
            ...existingPreview?.state?.locations,
            ...newLocation,
        },
    }

    setMultiModal(
        modalKey,

        FLOATING_PREVIEW,
        newPreview,
        show,
        true,
    )
}

export const hideFloatingPreview = () => {
    const existingPreviews: Array<FloaterModal> = Object.values(
        MODALSTATE.floater,
    ).filter(
        (floater: FloaterModal) =>
            floater.type === FLOATING_PREVIEW && floater.show,
    )
    existingPreviews.forEach((existingPreview) => {
        setMultiModal(
            generateModalKey(FLOATING_PREVIEW, existingPreview.state.id),

            FLOATING_PREVIEW,
            existingPreview.state,
            false,
            true,
        )
    })
}

const getSafeSize = (size: FloaterSize, increase: boolean) => {
    if (getScreenState().windowWidth < 1025) {
        // skip the "Medium" size on smaller screens
        if (size === FloaterSize.Medium)
            return increase ? FloaterSize.Large : FloaterSize.Small
    }
    return size
}
export const setFloaterSize = (
    idOrSlug: number | string,
    floaterType: string,
    size: FloaterSize,
) => {
    const [, id] = getSlugAndId(idOrSlug)
    const isFloatingRoom = floaterType === FLOATING_ROOM
    const modalKey = generateModalKey(floaterType, id)
    const floater = isFloatingRoom
        ? MODALSTATE.floating_room
        : MODALSTATE.floater[modalKey]
    const [orientation, portrait] = getFloaterOrientation()
    const increased = size > floater.state.size
    const foundSize = getSafeSize(size, increased)
    const [newLocation] = getNewExpandLocation(
        modalKey,
        floaterType,
        foundSize,
        floater.state.locations[orientation],
    )

    if (isFloatingRoom) {
        setModal(
            ModalKind.FloatingRoom,
            floaterType,
            {
                id: floater.state.id,
                expanded: (floater as FloatingRoomModal).state.expanded,
                minimized: floater.state.minimized,
                roomView: (floater as FloatingRoomModal).state.roomView,
                locations: {
                    ...floater.state.locations,
                    [orientation]: newLocation,
                },
                size: foundSize,
            },
            floater.show,
            true,
        )
    } else
        setMultiModal(
            modalKey,

            floaterType,
            {
                ...floater.state,
                sticky:
                    floaterType === QUICK_REPLY
                        ? portrait
                        : (floater as FloaterModal).state.sticky,
                locations: {
                    ...floater.state.locations,
                    [orientation]: newLocation,
                },
                size: foundSize,
            },
            floater.show,
            true,
        )
}

export const isModelVideoPublic = (id: number) => {
    const {
        isBroadcasting,
        isTruePrivate,
        isPrivateShow,
        isGroupShow,
        isClubShow,
        isOffline,
        isAway,
        banned,
        isKicked,
    } = getUser(id)
    if (!isBroadcasting) return false
    const inHiddenShow =
        isTruePrivate || isPrivateShow || isGroupShow || isClubShow
    const noShowType = isOffline || inHiddenShow || isAway
    return !noShowType && !banned && !isKicked
}

export const openQuickReply = (
    idOrSlug: number | string,
    overrideOptions?: FloaterState,
    location?: FloaterLocation,
) => {
    const [slug, id] = getSlugAndId(idOrSlug)

    const modalKey = generateModalKey(QUICK_REPLY, id)
    const options = {
        size: isTouchDevice
            ? FloaterSize.Small
            : isModelVideoPublic(id)
              ? FloaterSize.Large
              : FloaterSize.Small,
        ...overrideOptions,
    }

    if (!options.minimized) {
        clearPMUnreadCount(id)
    }
    const [orientation, portrait] = getFloaterOrientation()
    const otherOrientation = portrait ? 'landscape' : 'portrait'
    const sticky = portrait
    const isMobile = APPSTATE.app.isMobile

    if (!portrait && isMobile) {
        // we should create this minimized and then actually route to the full pm room experience.
        // this will handle landscape android phones and tablets, and ios phones
        // - all those devices are fine in portrait but landscape is too crushed
        // - ipad seems to handle landscape fine with its virtual keyboard handling
        setMultiModal(
            modalKey,

            QUICK_REPLY,
            {
                id,
                minimized: true,
                locations: {
                    [orientation]: { ...DEFAULT_LOCATION },
                    [otherOrientation]: { ...DEFAULT_LOCATION },
                },
                sticky,
                ...options,
            },
            true,
            false,
        )
        setModalStack(modalKey, { pushDown: true })

        // let this handle the rest too (clearing unread, setting recipient, etc)
        openMessage(slug)
        updateLocalStorage(slug)
        return
    }

    if (isMobile) {
        const pms = PMSTATE.chats
        // check to make sure chat head based quick replies are
        // limited to 2 at a time with `show` true
        // - show:false the one with the oldest last message?
        const existingQuickReplies = Object.values(MODALSTATE.floater).filter(
            (floater: FloaterModal) =>
                floater.show && floater.type === QUICK_REPLY,
        )

        if (existingQuickReplies.length > 1) {
            existingQuickReplies.sort((a, b) => {
                const messagesA = pms[a.state.id]
                const aTimestamp =
                    messagesA?.messages?.[messagesA?.messages?.length - 1]
                        ?.timestamp ?? 0
                const messagesB = pms[b.state.id]
                const bTimestamp =
                    messagesB?.messages?.[messagesB?.messages?.length - 1]
                        ?.timestamp ?? 0
                return bTimestamp - aTimestamp
            })
            const quickReplyToClose =
                existingQuickReplies[existingQuickReplies.length - 1]

            setShowMultiModal(
                generateModalKey(QUICK_REPLY, quickReplyToClose.state.id),
                false,
                false,
            )
        }
    }

    const existingModal = MODALSTATE.floater[modalKey]
    if (existingModal && existingModal.show) {
        const [newLocation] = getNewExpandLocation(modalKey, QUICK_REPLY)
        setMultiModal(
            modalKey,
            QUICK_REPLY,
            {
                ...existingModal.state,
                minimized: false,
                sticky,
                locations: {
                    [orientation]: { ...newLocation },
                    [otherOrientation]: { ...newLocation },
                },
                ...options,
            },
            true,
            true,
        )
        updateLocalStorage(slug)
        return
    }

    // get the location of the highest position floater in the stack and use its location
    const activeFloaterLocation = getTopFloaterLocation()
    const startingLocation = {
        x: activeFloaterLocation.x + 40,
        y: activeFloaterLocation.y + 40,
        speedX: 0,
        speedY: 0,
    }

    updateLocalStorage(slug, {
        open: true,
        lastInteraction: +new Date(),
        location: location ?? startingLocation,
        minimized: !!options?.minimized,
    })
    const [newLocation] = getNewExpandLocation(
        modalKey,
        QUICK_REPLY,
        options.size,
        location ?? startingLocation,
    )
    setMultiModal(
        modalKey,

        QUICK_REPLY,
        {
            id,
            minimized: false,
            // active: true,
            locations: {
                [orientation]: { ...newLocation },
                [otherOrientation]: { ...newLocation },
            },
            sticky,
            ...options,
        },
        true,
        true,
    )
    setModalStack(modalKey)
}

export const openMyWebcamFloater = () => {
    const existingModal = MODALSTATE.floater[MY_WEBCAM] ?? ({} as FloaterModal)
    const [orientation, isPortrait] = getFloaterOrientation()
    const otherOrientation = isPortrait ? 'landscape' : 'portrait'
    const [newLocation] = getNewExpandLocation(
        MY_WEBCAM,
        MY_WEBCAM,
        FloaterSize.Medium,
        {
            x: getScreenState().windowWidth - 375,
            y: 40,
            speedX: 0,
            speedY: 0,
        },
    )

    setMultiModal(
        MY_WEBCAM,

        MY_WEBCAM,
        {
            ...existingModal.state,
            minimized: false,
            locations: {
                [orientation]: { ...newLocation },
                [otherOrientation]: { ...newLocation },
            },
            close: true,
            size: FloaterSize.Large,
            stickyHeader: true,
            id: 0,
        },
        true,
        false,
    )
    setModalStack(MY_WEBCAM)
}

export const updateLocalStorage = (
    idOrSlug: string | number,
    quickReplyData?: Partial<Metadata['quickReply']>,
) => {
    const [slug, id] = getSlugAndId(idOrSlug)
    const modalKey = generateModalKey(QUICK_REPLY, id)
    const quickReply = MODALSTATE.floater[modalKey]
    const [orientation] = getFloaterOrientation()

    // console.trace('[updateLocalStorage]', slug, {
    //     open: quickReply?.show ?? false,
    //     lastInteraction: +new Date(),
    //     minimized: quickReply?.state?.minimized,
    //     location: quickReply?.state?.locations?.[orientation],
    //     ...quickReplyData,
    // })

    setRecipient(slug, {
        quickReply: {
            open: quickReply?.show ?? false,
            lastInteraction: +new Date(),
            minimized: quickReply?.state?.minimized,
            location: quickReply?.state?.locations?.[orientation],
            ...quickReplyData,
        },
    })
}

/**
 * Close the quick reply if we're Landscape and Mobile -
 * The quick replies don't actually show up anywhere but the left Nav,
 * without a way to close them in that situation
 */
export const checkAndCloseQuickReply = (idOrSlug: string | number) => {
    const [, portrait] = getFloaterOrientation()

    const isMobile = APPSTATE.app.isMobile

    const [slug, id] = getSlugAndId(idOrSlug)

    if (!portrait && isMobile) {
        const modalKey = generateModalKey(QUICK_REPLY, id)
        const existingModal = MODALSTATE.floater[modalKey]
        if (existingModal) {
            updateLocalStorage(slug, { open: false })
            setShowMultiModal(modalKey, false)
        }
    }
}

export const isQuickReplyOpen = (id: number, checkMinimized?: boolean) => {
    const quickReply = MODALSTATE.floater[generateModalKey(QUICK_REPLY, id)]
    if (checkMinimized) return quickReply?.show && !quickReply?.state?.minimized
    return quickReply?.show
}

export const updateFloaterLocation = (
    id: number,
    type: string,
    location: FloaterLocation,
) => {
    const isFloatingRoom = type === FLOATING_ROOM
    // avoid emitting since this is just data we store for spawning purposes
    const floater = isFloatingRoom
        ? MODALSTATE.floating_room
        : MODALSTATE.floater[generateModalKey(type, id)]
    if (floater) {
        const [orientation] = getFloaterOrientation()
        const currentOrientation = floater.state.locations[orientation]
        const speedY = location.y - currentOrientation.y
        // ensure we have a minimum x speed
        const speedX = location.x - currentOrientation.x
        floater.state.locations[orientation] = {
            x: location.x,
            y: location.y,
            speedX: isNaN(speedX) ? 0 : speedX,
            speedY: isNaN(speedY) ? 0 : speedY,
            width: currentOrientation.width,
            height: currentOrientation.height,
            state: currentOrientation.state,
        }
    }
}

export const minimizeFloater = (id: number, type: string, minimize = true) => {
    const modalKey = generateModalKey(type, id)
    const existingModal = MODALSTATE.floater[modalKey]

    if (!existingModal) return
    if (type === QUICK_REPLY) {
        const [slug] = getSlugAndId(id)
        if (minimize) {
            resumePMUnreadCount(id)
        } else {
            clearPMUnreadCount(id)
        }

        updateLocalStorage(slug, { minimized: minimize })

        const floatingRoomState = MODALSTATE.floating_room
            .state as FloatingRoomFloaterState
        if (MODALSTATE.floating_room.show && floatingRoomState.id === id) {
            // ensure we dont show video if we're in the room already
            existingModal.state.size = FloaterSize.Small
        }
    }
    const [orientation] = getFloaterOrientation()
    const currentLocation = existingModal.state.locations[orientation]
    const [newLocation] = minimize
        ? [currentLocation]
        : getNewExpandLocation(modalKey, type)

    setMultiModal(
        modalKey,

        type,
        {
            ...existingModal.state,
            minimized: minimize,
            locations: {
                ...existingModal.state.locations,
                [orientation]: newLocation,
            },
        },
        existingModal.show,
        true,
    )
    setModalStack(modalKey, { pushDown: true, immediate: true })
}

export const toggleFloaterVideo = (override?: boolean) => {
    setModal(
        ModalKind.FloatingRoom,
        FLOATING_ROOM,
        {
            ...MODALSTATE.floating_room.state,
            minimized: override ?? !MODALSTATE.floating_room.state.minimized,
        },
        true,
        true,
    )
}

export const toggleQuickReplySize = (id: number) => {
    const modalKey = generateModalKey(QUICK_REPLY, id)
    const existingModal = MODALSTATE.floater[modalKey]
    setMultiModal(
        modalKey,

        QUICK_REPLY,
        {
            ...existingModal.state,
            size:
                existingModal.state.size < FloaterSize.Large
                    ? FloaterSize.Large
                    : FloaterSize.Small,
        },
        existingModal.show,
    )
}

export const openFloatingRoom = (
    idOrSlug: number | string,
    roomView = RoomView.Room, // this may be too naive
) => {
    const [, id] = getSlugAndId(idOrSlug)
    const fullWindow = LAYOUTSTATE.layout.fullWindow
    const { portrait } = getScreenState()
    const currentId = MODALSTATE.floating_room.state.id
    const fullScreen =
        fullWindow ||
        portrait ||
        (MODALSTATE.floating_room.state.expanded ?? true)
    const { isBroadcasting = false, isAway = false } = getUser(id) || {}
    const videoHidden =
        currentId === id
            ? MODALSTATE.floating_room.state.minimized
            : roomView === RoomView.PM
              ? !isBroadcasting || isAway
              : false
    const newRoom: FloatingRoomFloaterState = {
        id,
        expanded: fullScreen,
        minimized: videoHidden,
        size: FloaterSize.Fullscreen,
        roomView,
        locations: {
            portrait: null,
            landscape: null,
        },
    }
    setModal(ModalKind.FloatingRoom, FLOATING_ROOM, newRoom, true, true)
}

export const closeFloatingRoom = (
    idOrSlug: string | number,
    { skipClose = false } = {},
) => {
    const [, id] = getSlugAndId(idOrSlug)

    if (MODALSTATE.floating_room.state.id === id) {
        if (!skipClose) closeRoom(id, { skipCloseIfPinned: true })
        const container = VIDEOSTATE[id]?.container
        if (container && container.firstElementChild)
            container.firstElementChild.classList.remove(
                'video-transition-source',
            )

        setModal(
            ModalKind.FloatingRoom,
            FLOATING_ROOM,
            MODALSTATE.floating_room.state,
            false,
            true,
        )
    }
}

export const isFloatingRoomPm = (idOrSlug: string | number) => {
    const [, id] = getSlugAndId(idOrSlug)

    if (MODALSTATE.floating_room.state.id === id) {
        return MODALSTATE.floating_room.state.roomView === RoomView.PM
    }
    return false
}

const getSafeStickyPreviewLocation = (coords?: {
    x: number
    y: number
}): [newLocation: FloaterLocation, changed: boolean] => {
    const previewLocation = calcPreviewLocation(FloaterSize.Small)
    if (coords) {
        previewLocation.x = coords.x
        previewLocation.y = coords.y
        return getNewExpandLocation(
            FLOATING_ROOM,
            FLOATING_ROOM,
            FloaterSize.Small,
            previewLocation,
        )
    }
    return [previewLocation, false]
}

export const convertFloatingRoomToSticky = (
    idOrSlug: number | string,
    coords?: { x: number; y: number },
    // shouldRoute = false,
) => {
    const [, id] = getSlugAndId(idOrSlug)

    const [previewLocation] = getSafeStickyPreviewLocation(coords)

    // if (shouldRoute) {
    //     // clear matching pinned room if this is coming from navigation rather than drag-to-preview
    //     const { pinnedRooms } = ROOMSTATE
    //     if (pinnedRooms.includes(id))
    //         pinRoom(id, { pinned: false, skipClose: true })

    //     // const newPath = buildRestorePath()
    //     // const newAsPath = buildRestoreAsPath()
    //     route(NewRouter.router.route.baseRoute)
    //     _setRoomOpenState(false, id)
    // }
    const { [id]: { isIntentionallyClosed = false } = {} } = PMSTATE.chats

    // check to see if we're a pm on desktop and we didn't close the PM intentinoally and we're not in some non-public show'
    if (
        MODALSTATE.floating_room.state.roomView === RoomView.PM &&
        !isTouchDevice &&
        !isIntentionallyClosed
    ) {
        const { isPublicShow, isBroadcasting } = getUser(id) || {}

        if (
            !MODALSTATE.floating_room.state.minimized &&
            !isPublicShow &&
            isBroadcasting
        ) {
            // if we're in a non-public show open the quick reply and the normal preview
            setModal(
                ModalKind.FloatingRoom,
                FLOATING_ROOM,
                {
                    ...MODALSTATE.floating_room.state,
                    id,
                    size: FloaterSize.Small,
                    locations: {
                        portrait: { ...previewLocation },
                        landscape: { ...previewLocation },
                    },
                },
                true,
                true,
            )
            openQuickReply(id, { size: FloaterSize.Small })
        } else {
            // only open a quick reply on desktop, while navigating away
            const options = MODALSTATE.floating_room.state.minimized
                ? { size: FloaterSize.Small }
                : undefined
            openQuickReply(id, options)
            closeFloatingRoom(id)
        }
    } else {
        setModal(
            ModalKind.FloatingRoom,
            FLOATING_ROOM,
            {
                ...MODALSTATE.floating_room.state,
                id,
                size: FloaterSize.Small,
                locations: {
                    portrait: { ...previewLocation },
                    landscape: { ...previewLocation },
                },
            },
            true,
            true,
        )
    }
}
